// Handles a Turbo Frame request the server answered with an error page instead of
// the frame — the record behind a lazy frame was deleted while the page sat open:
//
//   Error: The response (404) did not contain the expected
//   <turbo-frame id="task_41250_subtasks"> and will be ignored.
//
// (Honeybadger fault 54429: a tablet kept /tasks/41250 open for twelve minutes,
// the task was deleted meanwhile, and the lazy subtasks frame loaded on scroll.)
//
// Turbo's default is to throw, which nothing awaits — an unhandled rejection —
// and to leave the frame as it was, so a lazy frame's loading skeleton pulses
// forever. Instead the frame says what happened and offers a reload.
//
// Only error statuses are handled. A 2xx response without the frame is a
// mismatched frame id in our own markup, and that one should keep surfacing.

// The texts come from <meta name="turbo-frame-error-*"> in layouts/shared/_head, so they
// stay in de.yml. A layout without them keeps Turbo's default rather than showing an
// empty notice.
const metaText = (document, name) =>
  document.head.querySelector(`meta[name="turbo-frame-error-${name}"]`)?.getAttribute("content")

const noticeTexts = (document, status) => {
  const message = metaText(document, status === 404 ? "not-found" : "failed")
  const reload = metaText(document, "reload")
  return message && reload ? { message, reload } : null
}

const buildNotice = (document, { message, reload: reloadLabel }) => {
  const notice = document.createElement("div")
  notice.className =
    "rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-600 flex flex-wrap items-center gap-2"
  notice.setAttribute("role", "status")

  const text = document.createElement("span")
  text.textContent = message

  const reload = document.createElement("button")
  reload.type = "button"
  reload.className = "font-medium text-green-700 hover:text-green-800 underline"
  reload.textContent = reloadLabel
  reload.addEventListener("click", () => document.defaultView.location.reload())

  notice.append(text, reload)
  return notice
}

// Listener for `turbo:frame-missing`, dispatched on the <turbo-frame> itself.
export const handleFrameErrorResponse = (event) => {
  const response = event.detail && event.detail.response
  if (!response || response.ok) return

  const frame = event.target
  const texts = noticeTexts(frame.ownerDocument, response.status)
  if (!texts) return

  event.preventDefault()
  frame.replaceChildren(buildNotice(frame.ownerDocument, texts))
}

// Opt-in listeners for frames whose content a form needs before it can submit (the employee
// checklists of the settings tab). Wire them per frame with data-action — not globally:
// a frame that silently keeps its placeholder would leave such a form dead with no hint why.

// `turbo:frame-missing` after a redirect: the session expired and the frame request landed
// on the sign-in page. Follow it with a full visit, as a page navigation would have.
export const followFrameRedirect = (event) => {
  const response = event.detail && event.detail.response
  if (!response || !response.redirected) return

  event.preventDefault()
  event.detail.visit(response)
}

// `turbo:fetch-request-error`: the request never got an answer (the connection dropped).
// Turbo only logs it, leaving the loading placeholder up forever.
export const handleFrameNetworkError = (event) => {
  const frame = event.target
  const texts = noticeTexts(frame.ownerDocument)
  if (texts) frame.replaceChildren(buildNotice(frame.ownerDocument, texts))
}
